import { useCallback } from "react";
import {
  LIVE_SELECTION,
  captureChatTarget,
  resolveChatTarget,
} from "../lib/forecast/chatTarget.js";
import { persistOrHold } from "../store/heldMessages.js";
import { saveDraftEntries, DRAFT_CONV_ID } from "../lib/forecast/draft.js";
import { makeEntry } from "../lib/forecast/model.js";
import { tagAssistantTurn } from "../lib/chatMerge";
import { entryAppended } from "../lib/forecast/reducer.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { getRunState, runStore } from "../store/runStore.js";
import { selectDraftEntries } from "../store/selectors.js";

// Every read below goes through the LIVE store (getRunState), never a render-time snapshot:
// a line written on a forecast's completion tick (completion dispatched, the summary posted
// synchronously before any re-render) must see the run's just-stamped forecastId, and a line
// filed under the selection right after `activeSet` must land in the newly selected thread.
// A render-mirrored ref lagged both until the next render.

// A captured target, resolved BY ID against the live store (null when its run is gone).
// LIVE_SELECTION captures the selection as the line is WRITTEN (the chat panel renders the
// live activeRunId's conversation, so narration must land in THAT thread to be visible) and
// resolves it the same way; with nothing selected it is the draft.
function resolveTarget(target, caller) {
  const state = getRunState();
  if (target === LIVE_SELECTION) return resolveChatTarget(state, captureChatTarget(state));
  if (!target) {
    throw new TypeError(
      `${caller}: a target is required — pass the target captured where the work started ` +
        "(lib/forecast/chatTarget.ts), or LIVE_SELECTION from synchronous view code or tour narration.",
    );
  }
  return resolveChatTarget(state, target);
}

// Append `entry` at a resolved destination and persist it: a forecast's thread to its row
// (held until the row exists, for a forecast still being submitted), the draft to
// sessionStorage (the draft has no row yet, so that is the only way a pre-forecast line
// survives a reload; submit adopts the draft into the new forecast's conversation and clears
// it). `priorDraft` is the draft as it stood before this append.
function file(entry, dest, { persist, priorDraft }) {
  if (dest.draft) {
    runStore.dispatch(entryAppended(DRAFT_CONV_ID, entry));
    saveDraftEntries([...priorDraft, entry]);
    return;
  }
  runStore.dispatch(entryAppended(dest.convId, entry));
  if (persist) persistOrHold(dest.convId, dest.forecastId ?? null, entry);
}

// A line whose forecast is gone (deleted, cleared, access lost) is dropped: filing it under
// the selection is the misfile this exists to prevent, and appending it to the dead
// conversation would resurrect a thread nothing renders.
function dropped(caller) {
  recordUiEvent({ event: "ui.chat.line_dropped", reason: "target_gone", caller });
}

// The draft as the live store holds it now, so two lines filed back to back (no render
// between) both reach sessionStorage.
const draftNow = () => selectDraftEntries(getRunState());

/**
 * Chat message primitives: append user/assistant lines to a conversation (or the draft) and
 * persist them. Bound to the run-store singleton (`getRunState` / `runStore.dispatch`): no store
 * handles are threaded in.
 *
 * Every assistant line names its destination: `target` is either a {@link ChatTarget} captured
 * where the work started (lib/forecast/chatTarget.ts), resolved BY ID against the live store
 * when the line is written, or `LIVE_SELECTION`, which only synchronous view code and tour
 * narration (describing the screen) may pass.
 * There is no default: a reply that lands after an await must not follow the selection into
 * another forecast's thread.
 *
 * @param {{ tourExampleRunIdRef: React.MutableRefObject }} args
 * @returns {{ pushUserMsg: Function, pushSayerMsg: Function, appendEntry: Function }}
 */
export function useChatMessages({ tourExampleRunIdRef }) {
  // Append a user message to the chat log, persist it, and dispatch it to the store. It is
  // written at send, synchronously, so the selection IS its target; a chat turn passes the
  // target it captured so the question and its reply provably share a thread. `msg_id` joins
  // the row to the chat.send telemetry event (lib/chatTurn.js newMessageId).
  const pushUserMsg = useCallback((text, { msgId = null, target = LIVE_SELECTION } = {}) => {
    const userMsg = { role: "user", content: text };
    const entry = makeEntry({
      role: "user",
      content: text,
      ...(msgId ? { metadata: { msg_id: msgId } } : {}),
    });
    const dest = resolveTarget(target, "pushUserMsg");
    if (!dest) {
      dropped("pushUserMsg");
      return userMsg;
    }
    file(entry, dest, { persist: true, priorDraft: draftNow() });
    return userMsg;
  }, []);

  // Append a Sayer (assistant) message to `target`. kind:"tour" marks scripted narration —
  // styled as narration in ChatContent (_tour flag) and NOT persisted, so tour copy stays
  // session-only and the log self-cleans on next visit. kind:"notice" is an ephemeral
  // compare warning that is never filed, so it needs no target.
  const pushSayerMsg = useCallback(
    (text, { target, kind = "chat", legend, reportLink } = {}) => {
      const markers = {
        ...(kind === "tour" ? { _tour: true } : {}),
        ...(legend ? { _legend: true } : {}),
      };
      if (kind === "notice") return tagAssistantTurn(text, null, markers);
      const dest = resolveTarget(target, "pushSayerMsg");
      const msg = tagAssistantTurn(text, dest?.forecastId ?? null, markers);
      if (!dest) {
        dropped("pushSayerMsg");
        return msg;
      }
      // The onboarding tour's example forecast is the ONE exception to the guide-narration
      // persistence gate: it carries a real forecastId (eager-created in startTour) and
      // isTourExample:true, so its tour chat persists and round-trips on reload. All other
      // tour/guide narration stays session-only (rendered, not saved). isTourExample is
      // in-session only (not on the wire), so a reload-resumed run loses it — fall back to the
      // session ref that tracks the tour example's runId so post-resume narration keeps
      // persisting.
      const run = dest.run;
      const isExampleForecast = !!(
        run?.forecastId &&
        (run?.isTourExample || run?.runId === tourExampleRunIdRef.current)
      );
      // A tour legend step is guide-styled narration that ALSO renders the Color/Pie/Size key.
      // entryKind stays "guide" (kind tour wins) so it keeps the lightbulb + italic styling;
      // the legend card and the forecast summary's "Full Report" link are driven by metadata
      // flags, which ride through the message round-trip (entryToMessageRow keeps non-reserved
      // keys) and so survive reload — entryKind alone ("legend") would be clobbered to "guide".
      const entryKind = kind === "tour" ? "guide" : legend ? "legend" : null;
      const entryMeta = {
        ...(legend ? { legend: true } : {}),
        ...(reportLink ? { reportLink: true } : {}),
      };
      const entry = makeEntry({
        role: "assistant",
        kind: entryKind,
        content: text,
        // Only on the forecast SUMMARY, which `hasForecastSummaryMessage` matches by runId:
        // branches share their root's conversation, so a conversation-wide match let one run's
        // summary suppress every other's. Stamping every message would change what the chat
        // projection sees for narration that belongs to no run.
        ...(reportLink && run?.runId ? { runId: run.runId } : {}),
        ...(Object.keys(entryMeta).length ? { metadata: entryMeta } : {}),
      });
      // Tour narration in the draft is saved there too: the pre-forecast tour messages
      // (greeting, question-format) have no forecast row yet, so the draft is the only way they
      // survive a reload during the greeting/asking phase. They are adopted into the example
      // forecast's conversation (and the draft cleared) at submit, so post-submit reloads
      // restore from the DB instead — no double-restore.
      file(entry, dest, {
        persist: kind !== "tour" || isExampleForecast,
        priorDraft: draftNow(),
      });
      return msg;
    },
    [tourExampleRunIdRef],
  );

  // Append a pre-built entry (makeEntry) to `target` and persist it. Same targeting contract
  // as pushSayerMsg, for callers that build their own entry.
  const appendEntry = useCallback((entry, { target } = {}) => {
    const dest = resolveTarget(target, "appendEntry");
    if (!dest) {
      dropped("appendEntry");
      return;
    }
    file(entry, dest, { persist: true, priorDraft: draftNow() });
  }, []);

  return { pushUserMsg, pushSayerMsg, appendEntry };
}
